import { CreateWorkGroupRequest, CreateWorkSpaceRequest } from '@/pages/workgroup/type';
import { QuerySyntax } from '@tigergraph/tools-models';
import mixpanel from 'mixpanel-browser';

export enum EventStatus {
  Success = 'Success',
  Fail = 'Fail',
}

export enum AppToolType {
  GraphStudio = 'Graph Studio',
  Insights = 'Insights',
  AdminPortal = 'Admin Portal',
  GraphQL = 'GraphQL',
}

class AnalyticsService {
  private userEmail = '';
  private env = '';

  constructor() {
    this.init();
  }

  init() {
    this.env = window.location.origin.includes('tgcloud.io') ? 'production' : 'test';
    mixpanel.init(import.meta.env.VITE_MIX_Panel_Key);
    mixpanel.register({
      'Analytics Version': 1.1,
      Product: 'TigerGraph Savanna',
      Environment: this.env,
    });
  }

  /**
   * Check if the current user should be excluded from tracking.
   * Users with emails ending in "tigergraph.com" are excluded.
   */
  private shouldSkipTracking(): boolean {
    return this.env === 'production' && !!this.userEmail?.toLowerCase().endsWith('tigergraph.com');
  }

  /**
   * Send events to mixpanel.
   *
   * @param  {string} eventName event name
   * @param  {object} props? event properties
   */
  track(eventName: string, props?: object) {
    if (this.shouldSkipTracking()) {
      return;
    }

    try {
      mixpanel.track(eventName, props);
    } catch (error) {
      // ignore the error
    }
  }

  // login complete
  trackLoginComplete(userProfile: { tenantID: string; id: string; email: string }) {
    const { tenantID, id, email } = userProfile;

    this.userEmail = email;

    if (tenantID && id && !this.shouldSkipTracking()) {
      mixpanel.opt_in_tracking();
      mixpanel.identify(tenantID + '_' + id);
      this.track('Login Complete');
    }
  }

  trackCreateWorkspace(status: EventStatus, request: CreateWorkSpaceRequest) {
    this.track('Create Workspace', {
      status,
      ...request,
      solution_install_request: undefined,
      solution_name: request.solution_install_request?.name || 'none',
      addons: undefined,
    });
  }

  trackEnterSchemaDesigner() {
    this.track('Enter Schema Designer');
  }

  trackEnterGSQLEditor(from: string) {
    this.track('Enter Query Editor', { from });
  }

  trackEnterDataIngestion() {
    this.track('Enter Load Data');
  }

  trackEnterExploreGraph() {
    this.track('Enter Explore Graph');
  }

  trackEnterAppTools(toolType: AppToolType) {
    this.track('Enter App Tools', { toolType });
  }

  trackFinishSurveyForm(from: string, state: 'Success' | 'Fail') {
    this.track('Finish Survey Form', { from, state });
  }

  trackTutorialOpen(tutorialName: string) {
    this.track('Tutorial Open', { tutorialName });
  }

  trackQuickStart(moduleName: string) {
    this.track('Quick Start', { moduleName });
  }

  trackPageView(pageName: string) {
    this.track('Page View', { pageName });
  }

  trackWorkgroupCreation(status: EventStatus, request: CreateWorkGroupRequest) {
    this.track('Workgroup Creation', { status, ...request });
  }

  trackGraphCreation(status: EventStatus) {
    this.track('Graph Creation', { status });
  }

  trackVertexCreation(status: EventStatus) {
    this.track('Vertex Creation', { status });
  }

  trackSaveSchema(status: EventStatus) {
    this.track('Save Schema', { status });
  }

  trackDataLoading(status: EventStatus, step: string) {
    this.track('Data Loading', { status, step });
  }

  trackQueryCreation(syntax: QuerySyntax) {
    this.track('Query Creation', { syntax });
  }

  trackGSQLExecution(status: EventStatus) {
    this.track('GSQL Execution', { status });
  }

  trackExploreGraph(status: EventStatus, action: string) {
    this.track('Explore Graph', { status, action });
  }
}

const trackUtil = new AnalyticsService();
export default trackUtil;
